/**
 * Files (Settings): every video and picture the brand stores, for clean-up, and the storage meter
 * (section 5). Uploading mostly happens where it is needed (an item's Video and Artwork, the
 * header's Upload button); here too, for a batch. States as everywhere: Uploading 42%, Waiting for
 * the encoder, Encoding, Ready, Failed with the reason.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@web-ui/components/Button";
import DataTable from "@web-ui/components/DataTable";
import DataTableRow from "@web-ui/components/DataTableRow";
import Empty from "@web-ui/components/Empty";
import Field from "@web-ui/components/Field";
import Meter from "@web-ui/components/Meter";
import Notice from "@web-ui/components/Notice";
import Select from "@web-ui/components/Select";
import Tabs from "@web-ui/components/Tabs";
import { media, type Budget, type MediaAsset } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { lengthWords } from "@/forms/time";
import { bytes, relative } from "@/lib/format";
import { explainMediaError } from "@/media/errors";
import { FilePreview, fileThumb } from "@/media/file-preview";
import { ImageCropUpload, ROLE_WORDS } from "@/media/image-upload";
import { videoState, ENCODER_LINE } from "@/media/states";
import { LADDERS } from "@/media/upload-button";
import { useUploads } from "@/media/upload-center";
import { UploadStateChip, UploadStateLine } from "@/media/upload-state";
import { useStudio } from "@/shell/studio";
import type { ImageRole } from "@media-domain/images";

export function MediaLibrary({ client, canEdit }: { client: AdminClient; canEdit: boolean }) {
  const { assets, refresh, stateOf } = useUploads();
  const [budget, setBudget] = useState<Budget | null>(null);
  const [tab, setTab] = useState({ id: 0, name: "Videos" });
  const [message, setMessage] = useState<{ tone: "critical" | "positive"; text: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const loadBudget = useCallback(async () => { try { setBudget(await media.budget(client)); } catch { /* the meter waits */ } }, [client]);
  useEffect(() => { void loadBudget(); void refresh(); }, [loadBudget, refresh]);
  useEffect(() => { void loadBudget(); }, [assets, loadBudget]);

  const kind = tab.id === 0 ? "video" : "image";
  const shown = assets?.filter((a) => a.kind === kind).sort((a, b) => b.createdAt - a.createdAt) ?? null;

  return (
    <div className="flex flex-col gap-3">
      {message ? <Notice tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Notice> : null}
      {budget ? (
        <section className="card">
          <Meter label="Storage" value={budget.storedBytes + budget.reservedBytes} max={budget.budgetBytes}
            figure={`${bytes(budget.storedBytes + budget.reservedBytes)} of ${bytes(budget.budgetBytes)}`}
            note={`${bytes(budget.freeBytes)} free. An upload that would not fit is refused before anything is stored.`}
            tone={budget.freeBytes < budget.budgetBytes * 0.2 ? "warning" : "neutral"} />
        </section>
      ) : null}
      <Tabs items={[{ id: 0, name: "Videos" }, { id: 1, name: "Pictures" }]} value={tab} setValue={setTab} />
      {canEdit && kind === "video" ? <VideoUpload onError={(e) => setMessage({ tone: "critical", text: explainMediaError(e) })} /> : null}
      {canEdit && kind === "image" ? (
        <section className="card flex flex-col gap-3">
          <h3 className="text-sub-title-1 ps-card-inset">Add a picture</h3>
          <ImageCropUpload client={client} onDone={(_f, _a, existing) => { setMessage({ tone: "positive", text: existing ? "That picture was already here with this crop." : "The picture is ready to use." }); void refresh(); }} onError={(e) => setMessage({ tone: "critical", text: explainMediaError(e) })} />
        </section>
      ) : null}
      <section className="card">
        <DataTable label={tab.name} loading={shown === null} isEmpty={shown?.length === 0}
          empty={<Empty icon={kind === "video" ? "video" : "camera"} title={`No ${tab.name.toLowerCase()} yet`} />}
          cellsThead={<><th><span className="sr-only">Preview</span></th><th>Name</th><th>State</th><th className="max-md:hidden">Details</th><th className="max-md:hidden">Size</th><th className="max-lg:hidden">Added</th><th><span className="sr-only">Actions</span></th></>}>
          {shown?.map((a) => {
            const d = a.details as { durationSeconds?: number; ladder?: string; role?: string; sizes?: Array<{ width: number }> } | null;
            const thumb = fileThumb(a);
            const state = a.kind === "video" ? stateOf(a.id) ?? videoState(a) : null;
            const ladder = LADDERS.find((l) => l.value === d?.ladder)?.name;
            return (
              <DataTableRow key={a.id}>
                <td>{thumb ? <img src={thumb} alt="" className="h-12 w-12 rounded-lg bg-b-surface2 object-cover" loading="lazy" /> : <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-b-surface2 text-caption text-t-secondary" aria-hidden="true">{a.kind === "video" ? "▶" : ""}</span>}</td>
                <td className="text-body-2"><button type="button" className="text-start font-medium hover:underline focus-visible:underline" onClick={() => setOpen(a.id)} aria-label={`Preview ${a.title}`}>{a.title}</button></td>
                <td className="max-w-80">{state ? (state.final ? <UploadStateChip state={state} /> : <UploadStateLine state={state} />) : <span className="text-body-2 text-t-secondary">{a.status === "ready" ? "Ready" : a.status}</span>}{state?.key === "failed" ? <div className="text-caption text-t-secondary">{state.detail}</div> : null}</td>
                <td className="text-body-2 text-t-secondary max-md:hidden">
                  {a.kind === "video" ? [ladder, lengthWords(d?.durationSeconds)].filter(Boolean).join(" · ") : [d?.role ? ROLE_WORDS[d.role as ImageRole] ?? d.role : null].filter(Boolean).join(" · ")}
                </td>
                <td className="text-body-2 text-t-secondary max-md:hidden">{a.storedBytes ? bytes(a.storedBytes) : a.reservedBytes ? `≈ ${bytes(a.reservedBytes)}` : ""}</td>
                <td className="text-body-2 text-t-secondary max-lg:hidden">{relative(a.createdAt)}</td>
                <td><Button type="button" size="xs" variant="secondary" onClick={() => setOpen(a.id)}>Preview</Button></td>
              </DataTableRow>
            );
          })}
        </DataTable>
      </section>
      <FilePreview asset={assets?.find((a) => a.id === open) ?? null} onClose={() => setOpen(null)} />
    </div>
  );
}

function VideoUpload({ onError }: { onError: (e: unknown) => void }) {
  const { startVideo } = useUploads();
  const { pack } = useStudio();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [ladder, setLadder] = useState(LADDERS[pack.vertical === "film" ? 1 : 0]!);
  const input = useRef<HTMLInputElement>(null);
  const start = async () => {
    if (!file) return;
    try {
      await startVideo(file, title.trim() || file.name.replace(/\.[^.]+$/, ""), ladder.value);
      setFile(null); setTitle(""); if (input.current) input.current.value = "";
    } catch (e) { onError(e); }
  };
  return (
    <section className="card flex flex-col gap-3">
      <h3 className="text-sub-title-1 ps-card-inset">Upload a video</h3>
      <p className="text-body-2 text-t-secondary ps-card-inset">The original file (an export, not a download from a video site). {ENCODER_LINE}</p>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1 text-body-2">Video file
          <input ref={input} type="file" accept="video/*" className="text-body-2" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, "")); }} />
          {file ? <span className="text-caption text-t-tertiary">{bytes(file.size)}</span> : null}
        </label>
        <Field label="Name" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="flex flex-col gap-1">
          <Select label="Quality" value={ladder} options={LADDERS} onChange={(o) => setLadder(LADDERS[o.id]!)} />
          <p className="text-caption text-t-secondary">{ladder.note}</p>
        </div>
      </div>
      <div><Button type="button" variant="primary" disabled={!file} onClick={() => void start()}>Upload</Button></div>
    </section>
  );
}
